import { z } from 'zod'
import { ghlOpportunityStatusSchema } from '../enums/ghl-opportunity-status.enum.js'
import { ghlFieldMapSchema, ghlStageRefSchema } from '../dto/gohighlevel.dto.js'
import { ghlNameSchema } from '../dto/lead-stats.dto.js'
import { GHL_FUNNEL_TARGETS } from '../enums/funnel-stage.enum.js'
import { appointmentsMetricDefinitionInputSchema, leadsMetricDefinitionInputSchema } from './lead-stats.input.js'
import { GHL_FUNNEL_PIPELINES_MAX, GHL_MAX_LINKS_PER_EVENT, GHL_NAME_FORMAT_MAX } from '../constants/gohighlevel.js'
import { validateNameFormat } from '../helpers/event-name-format.js'

/** A private integration key, as pasted. Never echoed back. */
const ghlKeySchema = z.string().trim().min(8, 'Paste the whole key.').max(500, 'That key is too long.')

export const createGhlConnectionInputSchema = z
  .object({
    /** The Location ID, or a GoHighLevel link containing it (see `extractGhlLocationId`). */
    location: z.string().trim().min(1, 'Enter the Location ID.').max(300),
    key: ghlKeySchema,
    trackId: z.string().uuid().nullable().optional(),
  })
  .strict()
export type CreateGhlConnectionInput = z.infer<typeof createGhlConnectionInputSchema>

/**
 * A connection's funnel mapping as it is saved, whole. Strict all the way down:
 * a lenient parse would strip a definition of the other kind's keys, and a
 * leads definition with every list empty means every lead.
 */
export const ghlFunnelMapInputSchema = z
  .object({
    pipelines: z.array(ghlNameSchema).max(GHL_FUNNEL_PIPELINES_MAX),
    stages: z.partialRecord(
      z.enum(GHL_FUNNEL_TARGETS),
      z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('leads'), definition: leadsMetricDefinitionInputSchema }).strict(),
        z.object({ kind: z.literal('appointments'), definition: appointmentsMetricDefinitionInputSchema }).strict(),
      ]),
    ),
  })
  .strict()
export type GhlFunnelMapInput = z.infer<typeof ghlFunnelMapInputSchema>

export const updateGhlConnectionInputSchema = z
  .object({
    key: ghlKeySchema.optional(),
    trackId: z.string().uuid().nullable().optional(),
    // Strict copies of the response shapes: a request body rejects keys it doesn't know.
    pickerStages: z.array(ghlStageRefSchema.strict()).max(100).optional(),
    pickerStatuses: z.array(ghlOpportunityStatusSchema).min(1, 'Show at least one result.').optional(),
    fieldMap: ghlFieldMapSchema.strict().optional(),
    funnelMap: ghlFunnelMapInputSchema.optional(),
  })
  .strict()
export type UpdateGhlConnectionInput = z.infer<typeof updateGhlConnectionInputSchema>

export const updateGhlNameFormatInputSchema = z
  .object({
    /** Null goes back to the default format. */
    nameFormat: z.string().trim().min(1, 'Enter a format.').max(GHL_NAME_FORMAT_MAX).nullable(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.nameFormat === null) return
    const problem = validateNameFormat(value.nameFormat)
    if (problem !== null) ctx.addIssue({ code: 'custom', message: problem, path: ['nameFormat'] })
  })
export type UpdateGhlNameFormatInput = z.infer<typeof updateGhlNameFormatInputSchema>

/**
 * One opportunity to link an event to, as the picker showed it. It comes from
 * the browser, so it is checked for shape here and the connection must be the
 * venue's; nothing else in it is trusted beyond the caller's own company.
 */
export const ghlOpportunityRefSchema = z
  .object({
    connectionId: z.string().uuid(),
    opportunityId: z.string().trim().min(1).max(64),
    contactId: z.string().trim().min(1).max(64),
    /** The couple's email as GoHighLevel had it. Only a hash of it is kept, to recognize them again. */
    email: z.string().trim().max(320).nullish(),
  })
  .strict()
export type GhlOpportunityRef = z.infer<typeof ghlOpportunityRefSchema>

/** Which opportunity to read a new event's details from. */
export const ghlPrefillQuerySchema = z
  .object({
    connectionId: z.string().uuid(),
    opportunityId: z.string().trim().min(1).max(64),
  })
  .strict()
export type GhlPrefillQuery = z.infer<typeof ghlPrefillQuerySchema>

/** What to fill in from the lead on an event that is missing it. The event's name is never changed. */
export const ghlFillInputSchema = z
  .object({ client: z.boolean(), guestCount: z.boolean(), notes: z.boolean() })
  .strict()
export type GhlFillInput = z.infer<typeof ghlFillInputSchema>

/**
 * Set an existing event's GoHighLevel links: the couple's opportunities, or none
 * to unlink. `fill` asks for what the event is missing to be taken from the lead.
 */
export const putEventGhlLinksInputSchema = z
  .object({
    opportunities: z.array(ghlOpportunityRefSchema).max(GHL_MAX_LINKS_PER_EVENT),
    fill: ghlFillInputSchema.optional(),
  })
  .strict()
export type PutEventGhlLinksInput = z.infer<typeof putEventGhlLinksInputSchema>

/** Settle a review item by linking the event to a couple: Clef's pick, or another. */
export const resolveGhlReviewInputSchema = z
  .object({
    opportunities: z.array(ghlOpportunityRefSchema).min(1).max(GHL_MAX_LINKS_PER_EVENT),
  })
  .strict()
export type ResolveGhlReviewInput = z.infer<typeof resolveGhlReviewInputSchema>
